import { __assign, __read, __spread } from "tslib";
import { scaleLinear, scaleUtc } from 'd3-scale';
import { v4 as uuid } from 'uuid';
import { transparentize } from 'polished';
import { calculateRange, calculateScaleDomain, getKeysDifference, transformToPercent, normalizeDate, sortKeysByValuesSum, getOffsetRangeColor, } from '@keen.io/charts-utils';
import { colors as colorPalette } from '@keen.io/colors';
import calculateLineStackedRange from './calculate-line-stacked-range';
import { calculateStackData, calculateStackAreaData, calculateMaxMinSeriesValue, } from './data.utils';
import { generateLineMarks } from './marks.utils';
import { generateSteps } from './steps.utils';
import { calculatePath } from './lines.utils';
import { generateSeriesBlockHeight, generateAreaGradient, } from './gradient.utils';
import { calculateArea } from './area.utils';
import { GROUPED_GRADIENT, STACKED_GRADIENT } from '../constants';
export var generateGroupedLines = function (_a) {
    var data = _a.data, keys = _a.keys, disabledKeys = _a.disabledKeys, dimension = _a.dimension, margins = _a.margins, minValue = _a.minValue, maxValue = _a.maxValue, labelSelector = _a.labelSelector, colors = _a.colors, markRadius = _a.markRadius, strokeWidth = _a.strokeWidth, curve = _a.curve, areaMode = _a.areaMode, xScaleSettings = _a.xScaleSettings, activeKey = _a.activeKey, sortedKeys = _a.sortedKeys, _b = _a.dataSeriesOffset, dataSeriesOffset = _b === void 0 ? [0, colors.length] : _b;
    var stepMode = curve === 'step';
    var filteredKeys = disabledKeys
        ? getKeysDifference(keys, disabledKeys)
        : keys;
    var _c = calculateRange(data, minValue, maxValue, filteredKeys), minimum = _c.minimum, maximum = _c.maximum;
    var marks = [];
    var steps = [];
    var areas = [];
    var lines = [];
    var gradientBlocks = [];
    var offsetMarks = [];
    var offsetAreas = [];
    var offsetLines = [];
    var offsetGradientBlocks = [];
    var precision = xScaleSettings.precision;
    var dateNormalizer = function (date) { return normalizeDate(date, precision); };
    var localizedData = data.map(function (item) {
        var _a;
        return (__assign(__assign({}, item), (_a = {}, _a[labelSelector] = dateNormalizer(item[labelSelector]), _a)));
    });
    var _d = __read(localizedData, 1), first = _d[0];
    var xScale = scaleUtc()
        .range([margins.left, dimension.width - margins.right])
        .domain([
        first[labelSelector],
        localizedData[localizedData.length - 1][labelSelector],
    ]);
    var yScale = scaleLinear()
        .range([dimension.height - margins.bottom, margins.top])
        .domain([minimum, maximum]);
    calculateScaleDomain(yScale, minimum, maximum);
    var minMaxSeriesValues = {};
    if (areaMode) {
        minMaxSeriesValues = calculateMaxMinSeriesValue(localizedData, labelSelector);
    }
    keys.forEach(function (keyName) {
        var idx = sortedKeys.indexOf(keyName);
        var inOffsetRange = idx >= dataSeriesOffset[0] && idx < dataSeriesOffset[1];
        var generateLine = calculatePath(curve, xScale, yScale, labelSelector, keyName);
        if (disabledKeys && !disabledKeys.includes(keyName)) {
            var isKeyActive = activeKey && keyName !== activeKey;
            var color = getOffsetRangeColor(idx, colors, dataSeriesOffset, transparentize(0.3, colorPalette.gray[300]));
            if (idx === 0)
                steps.push.apply(steps, __spread(generateSteps(localizedData, xScale, yScale, labelSelector, keys[0])));
            if (inOffsetRange) {
                offsetMarks.push.apply(offsetMarks, __spread(generateLineMarks(localizedData, xScale, yScale, labelSelector, color, keyName, markRadius)));
                offsetLines.push({
                    key: keyName,
                    selector: [idx, keyName],
                    d: generateLine(localizedData),
                    color: color,
                    strokeWidth: strokeWidth,
                });
            }
            else {
                marks.push.apply(marks, __spread(generateLineMarks(localizedData, xScale, yScale, labelSelector, color, keyName, markRadius)));
                lines.push({
                    key: keyName,
                    selector: [idx, keyName],
                    d: generateLine(localizedData),
                    color: color,
                    strokeWidth: strokeWidth,
                });
            }
            if (areaMode) {
                var _a = minMaxSeriesValues[keyName], minKeyNameValue = _a.min, maxKeyNameValue = _a.max;
                var isNegativeSeries = minimum < 0 && maximum <= 0;
                var generateArea = calculateArea(curve, xScale, yScale, labelSelector, keyName, minValue, maxValue, isNegativeSeries);
                if (inOffsetRange) {
                    offsetGradientBlocks.push(__assign({ x: margins.left, width: dimension.width - margins.right - margins.left }, generateSeriesBlockHeight(minimum, maximum, yScale, minKeyNameValue, maxKeyNameValue, isNegativeSeries)));
                    offsetAreas.push(__assign({ id: uuid(), d: generateArea(localizedData) }, generateAreaGradient(minKeyNameValue, maxKeyNameValue, getOffsetRangeColor(idx, colors, dataSeriesOffset, transparentize(0.5, colorPalette.gray[100])), isKeyActive
                        ? GROUPED_GRADIENT.disabled.min
                        : GROUPED_GRADIENT.default.min, isKeyActive
                        ? GROUPED_GRADIENT.disabled.max
                        : GROUPED_GRADIENT.default.max)));
                }
                else {
                    gradientBlocks.push(__assign({ x: margins.left, width: dimension.width - margins.right - margins.left }, generateSeriesBlockHeight(minimum, maximum, yScale, minKeyNameValue, maxKeyNameValue, isNegativeSeries)));
                    areas.push(__assign({ id: uuid(), d: generateArea(localizedData) }, generateAreaGradient(minKeyNameValue, maxKeyNameValue, getOffsetRangeColor(idx, colors, dataSeriesOffset, transparentize(0.5, colorPalette.gray[100])), isKeyActive
                        ? GROUPED_GRADIENT.disabled.min
                        : GROUPED_GRADIENT.default.min, isKeyActive
                        ? GROUPED_GRADIENT.disabled.max
                        : GROUPED_GRADIENT.default.max)));
                }
            }
        }
    });
    return {
        stepMode: stepMode,
        steps: steps,
        marks: __spread(marks, offsetMarks),
        lines: __spread(lines, offsetLines),
        xScale: xScale,
        yScale: yScale,
        areas: __spread(areas, offsetAreas),
        localizedData: localizedData,
        gradientBlocks: __spread(gradientBlocks, offsetGradientBlocks),
    };
};
export var generateStackLines = function (_a) {
    var data = _a.data, keys = _a.keys, disabledKeys = _a.disabledKeys, dimension = _a.dimension, margins = _a.margins, minValue = _a.minValue, maxValue = _a.maxValue, labelSelector = _a.labelSelector, colors = _a.colors, markRadius = _a.markRadius, strokeWidth = _a.strokeWidth, curve = _a.curve, areaMode = _a.areaMode, stackMode = _a.stackMode, groupMode = _a.groupMode, xScaleSettings = _a.xScaleSettings, activeKey = _a.activeKey, dataSeriesOffset = _a.dataSeriesOffset;
    var stepMode = curve === 'step';
    var filteredKeys = disabledKeys
        ? getKeysDifference(keys, disabledKeys)
        : keys;
    var precision = xScaleSettings.precision;
    var dateNormalizer = function (date) { return normalizeDate(date, precision); };
    var localizedData = data.map(function (item) {
        var _a;
        return (__assign(__assign({}, item), (_a = {}, _a[labelSelector] = dateNormalizer(item[labelSelector]), _a)));
    });
    var normalizeData = groupMode === 'stacked' && stackMode === 'percent'
        ? transformToPercent(localizedData, filteredKeys)
        : localizedData;
    var newData = calculateStackData(normalizeData, labelSelector, filteredKeys);
    var _b = calculateLineStackedRange(normalizeData, minValue, maxValue, filteredKeys), minimum = _b.minimum, maximum = _b.maximum;
    var percentMin = minimum < -100 ? -100 : minimum;
    var percentMax = maximum > 100 ? 100 : maximum;
    minimum = stackMode === 'percent' ? percentMin : minimum;
    maximum = stackMode === 'percent' ? percentMax : maximum;
    var marks = [];
    var steps = [];
    var areas = [];
    var lines = [];
    var gradientBlocks = [];
    var _c = __read(newData, 1), first = _c[0];
    var xScale = scaleUtc()
        .range([margins.left, dimension.width - margins.right])
        .domain([first[labelSelector], newData[newData.length - 1][labelSelector]]);
    var yScale = scaleLinear()
        .range([dimension.height - margins.bottom, margins.top])
        .domain([minimum, maximum]);
    calculateScaleDomain(yScale, minimum, maximum);
    var minMaxSeriesValues = {};
    var areaData = {};
    if (areaMode) {
        areaData = calculateStackAreaData(normalizeData, labelSelector, filteredKeys);
        minMaxSeriesValues = calculateMaxMinSeriesValue(__spread(areaData.firstDataPart, areaData.secondDataPart), labelSelector);
    }
    keys.forEach(function (keyName, idx) {
        var generatePath = calculatePath(curve, xScale, yScale, labelSelector, keyName);
        if (disabledKeys && !disabledKeys.includes(keyName)) {
            var isKeyActive = activeKey && keyName !== activeKey;
            var color = getOffsetRangeColor(idx, colors, dataSeriesOffset, transparentize(0.5, colorPalette.gray[300]));
            if (idx === 0)
                steps.push.apply(steps, __spread(generateSteps(newData, xScale, yScale, labelSelector, keys[0])));
            marks.push.apply(marks, __spread(generateLineMarks(newData, xScale, yScale, labelSelector, color, keyName, markRadius)));
            lines.push({
                key: keyName,
                selector: [idx, keyName],
                d: generatePath(newData),
                color: color,
                strokeWidth: strokeWidth,
            });
            if (areaMode) {
                var isNegativeSeries = minimum < 0 && maximum <= 0;
                var _a = minMaxSeriesValues[keyName], minKeyNameValue = _a.min, maxKeyNameValue = _a.max;
                gradientBlocks.push(__assign({ x: margins.left, width: dimension.width - margins.right - margins.left }, generateSeriesBlockHeight(minimum, maximum, yScale, minKeyNameValue, maxKeyNameValue, isNegativeSeries)));
                areas.push(__assign(__assign({ id: uuid() }, generateAreaGradient(minKeyNameValue, maxKeyNameValue, getOffsetRangeColor(idx, colors, dataSeriesOffset, transparentize(0.5, colorPalette.gray[100])), isKeyActive
                    ? STACKED_GRADIENT.disabled.min
                    : STACKED_GRADIENT.default.min, isKeyActive
                    ? STACKED_GRADIENT.disabled.max
                    : STACKED_GRADIENT.default.max)), { d: generatePath(areaData.firstDataPart) + " L" + generatePath(areaData.secondDataPart).substring(1) }));
            }
        }
    });
    return {
        stepMode: stepMode,
        steps: steps,
        marks: marks,
        lines: lines,
        xScale: xScale,
        yScale: yScale,
        areas: areas,
        gradientBlocks: gradientBlocks,
        localizedData: localizedData,
    };
};
export var generateLines = function (options) {
    var groupMode = options.groupMode, stackMode = options.stackMode, data = options.data, keys = options.keys, activeKey = options.activeKey;
    var areaKeys = sortKeysByValuesSum(data, keys);
    var sortedKeys = __spread(areaKeys);
    if (activeKey) {
        var activeKeyIndex = areaKeys.indexOf(activeKey);
        if (activeKeyIndex > -1)
            areaKeys.splice(activeKeyIndex, 1);
        areaKeys.push(activeKey);
    }
    return groupMode === 'grouped' &&
        (stackMode === 'normal' || stackMode === 'percent')
        ? generateGroupedLines(__assign(__assign({}, options), { keys: areaKeys, sortedKeys: sortedKeys }))
        : generateStackLines(__assign(__assign({}, options), { keys: sortedKeys }));
};
